import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { Video, Radio, Play, Pause, Loader2, Activity, Zap, AlertTriangle, Eye, Cpu, Link2, Upload, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { n: 1, label: "Ingest & Sync", icon: Link2 },
  { n: 2, label: "Vision Extraction", icon: Eye },
  { n: 3, label: "Feature & Event", icon: Activity },
  { n: 4, label: "OCTON Fusion", icon: Cpu },
  { n: 5, label: "Coach Advisory", icon: Zap },
  { n: 6, label: "Confidence Gate", icon: AlertTriangle },
];
const CADENCES = [{ label: "Off", v: 0 }, { label: "10s", v: 10 }, { label: "20s", v: 20 }, { label: "30s", v: 30 }];
const confColor = (c) => ({ high: "#33d17a", medium: "#ffb800", low: "#ff6b6b" }[c] || "#8a8f98");
const CHUNK = 4 * 1024 * 1024;

const Section = ({ id, label, state = "ready", children, testid }) => (
  <div data-octon-section={id} data-octon-label={label} data-octon-state={state} data-testid={testid}
    className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">{children}</div>
);

// STEP 2 visual: OCTON's detected shape/press drawn on the exact frame it read.
const Overlay = ({ ov }) => {
  if (!ov) return null;
  const line = (y, color, label) => (y == null ? null : (
    <>
      <line x1="2" y1={y * 100} x2="98" y2={y * 100} stroke={color} strokeWidth="0.5" strokeDasharray="2 1.5" opacity="0.85" />
      <text x="3" y={y * 100 - 1} fill={color} fontSize="2.6" opacity="0.95">{label}</text>
    </>
  ));
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" data-testid="pitch-overlay">
      {ov.press_zone && (
        <rect x={ov.press_zone.x * 100} y={ov.press_zone.y * 100} width={ov.press_zone.w * 100} height={ov.press_zone.h * 100}
          fill="#00e5ff" opacity="0.14" stroke="#00e5ff" strokeWidth="0.4" />
      )}
      {line(ov.team_block_y, "#00e5ff", "our line")}
      {line(ov.opp_block_y, "#ff6ad5", "their line")}
      {Array.isArray(ov.ball) && (
        <circle cx={ov.ball[0] * 100} cy={ov.ball[1] * 100} r="1.4" fill="#ccff00" stroke="#000" strokeWidth="0.3" />
      )}
    </svg>
  );
};

export default function LiveVideo() {
  const [mode, setMode] = useState("url");       // url | upload
  const [url, setUrl] = useState("");
  const [team, setTeam] = useState("Benfica");
  const [opponent, setOpponent] = useState("Porto");
  const [feed, setFeed] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [cadence, setCadence] = useState(20);
  const [running, setRunning] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [narrate, setNarrate] = useState(true);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const clockRef = useRef(0);
  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const srcRef = useRef("");                       // resolved url/token to analyse
  const [rec, setRec] = useState(null);            // {id} while recording
  const [recStat, setRecStat] = useState(null);
  const recTimerRef = useRef(null);

  const startRec = useCallback(async () => {
    try {
      const { data } = await api.post("/video/record/start", { url: srcRef.current });
      setRec({ id: data.rec_id }); toast.success("Audit capture started");
      clearInterval(recTimerRef.current);
      recTimerRef.current = setInterval(async () => {
        try { const { data: s } = await api.get(`/video/record/${data.rec_id}/status`); setRecStat(s); } catch (_) {}
      }, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Recording failed to start"); }
  }, []);

  const stopRec = useCallback(async () => {
    if (!rec) return;
    clearInterval(recTimerRef.current);
    try {
      const { data } = await api.post(`/video/record/${rec.id}/stop`);
      srcRef.current = data.url;
      setFeed({ ...data, is_live: false });   // switch to the finalised capture — now scrub any timecode
      clockRef.current = 0;
      toast.success(`Capture saved (${data.size_mb} MB, ${data.duration ? Math.round(data.duration) + "s" : "n/a"}) — seek any moment for the audit`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Stop failed"); }
    finally { setRec(null); setRecStat(null); }
  }, [rec]);

  const connectUrl = useCallback(async () => {
    if (!url.trim()) { toast.error("Paste a video or stream URL first"); return; }
    setConnecting(true); setErr(null); setFeed(null); setResult(null); clockRef.current = 0;
    try {
      const { data } = await api.post("/video/resolve", { url: url.trim() });
      srcRef.current = url.trim(); setFeed(data);
      toast.success(`Connected: ${data.title || "feed"}${data.is_live ? " (LIVE)" : ""}`);
    } catch (e) {
      const m = e?.response?.data?.detail || "Could not open that source";
      setErr(m); toast.error(m);
    } finally { setConnecting(false); }
  }, [url]);

  const onUpload = useCallback(async (file) => {
    if (!file) return;
    setConnecting(true); setErr(null); setFeed(null); setResult(null); clockRef.current = 0; setUploadPct(0);
    try {
      const { data: init } = await api.post("/video/upload/init", { filename: file.name });
      const id = init.upload_id;
      for (let off = 0; off < file.size; off += CHUNK) {
        const blob = file.slice(off, Math.min(off + CHUNK, file.size));
        await api.put(`/video/upload/${id}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
        setUploadPct(Math.round((Math.min(off + CHUNK, file.size) / file.size) * 100));
      }
      const { data } = await api.post(`/video/upload/${id}/complete`);
      srcRef.current = data.url; setFeed(data);
      toast.success(`Uploaded: ${file.name}`);
    } catch (e) {
      const m = e?.response?.data?.detail || "Upload failed";
      setErr(m); toast.error(m);
    } finally { setConnecting(false); setUploadPct(null); }
  }, []);

  const speak = useCallback((a) => {
    if (!narrate || !a || a.confidence === "low") return;
    const top = (a.advisory && a.advisory[0]?.instruction) || a.tactical_read || a.scene;
    const fat = a.fatigue_flags && a.fatigue_flags[0];
    const line = fat ? `Fatigue flag: ${fat.player}. ${fat.observation}. ${top || ""}` : top;
    if (!line) return;
    window.dispatchEvent(new CustomEvent("octon:video-narrate", { detail: { text: String(line).slice(0, 320) } }));
  }, [narrate]);

  const analyzeOnce = useCallback(async () => {
    if (busyRef.current || !feed) return null;
    busyRef.current = true; setAnalyzing(true); setErr(null);
    try {
      const body = { url: srcRef.current, team, opponent };
      if (!feed.is_live) body.at_seconds = clockRef.current;
      const { data } = await api.post("/video/analyze", body, { timeout: 120000 });
      setResult(data);
      window.dispatchEvent(new CustomEvent("octon:video-analysis", { detail: data }));
      speak(data.analysis);
      if (!feed.is_live) clockRef.current += Math.max(6, cadence || 10);
      return data;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Frame read failed"); return null;
    } finally { busyRef.current = false; setAnalyzing(false); }
  }, [feed, team, opponent, cadence, speak]);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (running && feed && cadence > 0) {
      analyzeOnce();
      timerRef.current = setInterval(analyzeOnce, cadence * 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [running, feed, cadence, analyzeOnce]);

  useEffect(() => {
    const handler = async (e) => {
      const r = await analyzeOnce();
      e.detail?.resolve?.(r ? { ok: true, analysis: r.analysis, is_live: r.is_live } : { ok: false, error: err || "read failed" });
    };
    window.addEventListener("octon:video-read-now", handler);
    return () => window.removeEventListener("octon:video-read-now", handler);
  }, [analyzeOnce, err]);

  const a = result?.analysis || null;
  const flags = (a?.fatigue_flags) || [];
  const activeStep = analyzing ? (feed ? 2 : 1) : a ? (a.confidence === "low" ? 6 : 5) : feed ? 1 : 0;

  return (
    <div className="px-6 pb-28 pt-20 sm:px-10 lg:px-16" data-testid="live-video-page"
      data-octon-fixture={feed ? "video" : ""} data-octon-fixture-label={feed ? `${team} — live video` : ""}>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-400/30 bg-cyan-400/10"><Video className="txt-cyan" size={22} /></div>
        <div>
          <h1 className="font-head text-3xl font-black tracking-tight sm:text-4xl">Live Video Co-Pilot</h1>
          <p className="text-sm text-white/45">Vision-fed tactical reads, fused with OCTON's real squad data & belief engine — advice in real time.</p>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2" data-testid="pipeline-rail">
        {STEPS.map((s) => {
          const on = activeStep >= s.n && activeStep > 0, live = activeStep === s.n;
          return (
            <div key={s.n} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-[11px] transition ${live ? "border-cyan-400/50 bg-cyan-400/10 text-white" : on ? "border-white/15 bg-white/[0.03] text-white/70" : "border-white/8 text-white/35"}`}>
              <s.icon size={13} className={live ? "txt-cyan" : ""} /><span className="font-tac tracking-wide">STEP {s.n}</span>
              <span className="hidden sm:inline">· {s.label}</span>{live && <Loader2 size={11} className="animate-spin txt-cyan" />}
            </div>
          );
        })}
      </div>

      <Section id="video source feed connect upload" label="Video Source" testid="video-source-section">
        <div className="mb-3 flex items-center gap-2 text-[10px] font-tac tracking-widest text-white/40"><Link2 size={12} /> STEP 0-1 · AUTHORISED SOURCE</div>
        <div className="mb-3 flex gap-2">
          {[["url", "Live / URL"], ["upload", "Upload a video"]].map(([m, l]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
              className={`rounded-lg px-3 py-1.5 text-[12px] ${mode === m ? "bg-cyan-400/20 text-cyan-200 border border-cyan-400/40" : "border border-white/10 text-white/50 hover:text-white"}`}>{l}</button>
          ))}
        </div>

        {mode === "url" ? (
          <div className="grid gap-3 md:grid-cols-[1fr_auto]">
            <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="video-url-input"
              placeholder="Live/recorded feed URL — YouTube live, HLS (.m3u8), MP4 or RTMP"
              className="w-full rounded-xl border border-white/12 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-cyan-400/40 focus:outline-none" />
            <button onClick={connectUrl} disabled={connecting} data-testid="connect-feed-btn"
              className="flex items-center justify-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-400/15 px-5 py-3 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-400/25 disabled:opacity-50">
              {connecting ? <Loader2 size={16} className="animate-spin" /> : <Radio size={16} />} Connect feed
            </button>
          </div>
        ) : (
          <label data-testid="upload-dropzone" className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-black/30 py-8 text-center hover:border-cyan-400/40">
            <Upload size={22} className="text-white/50" />
            <span className="text-sm text-white/70">Click to upload a match clip (MP4/MOV/WebM)</span>
            <span className="text-[11px] text-white/35">Chunked upload — large clips are fine. Best source for rehearsal (instant seeking).</span>
            <input type="file" accept="video/*" className="hidden" data-testid="video-file-input"
              onChange={(e) => onUpload(e.target.files?.[0])} />
            {uploadPct != null && <span className="mt-1 text-[12px] text-cyan-300" data-testid="upload-progress">Uploading… {uploadPct}%</span>}
          </label>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2"><span className="text-[11px] text-white/40">Home</span>
            <input value={team} onChange={(e) => setTeam(e.target.value)} data-testid="video-team-input" className="w-28 rounded-lg border border-white/12 bg-black/40 px-3 py-1.5 text-sm text-white focus:border-cyan-400/40 focus:outline-none" /></div>
          <div className="flex items-center gap-2"><span className="text-[11px] text-white/40">Away</span>
            <input value={opponent} onChange={(e) => setOpponent(e.target.value)} data-testid="video-opponent-input" className="w-28 rounded-lg border border-white/12 bg-black/40 px-3 py-1.5 text-sm text-white focus:border-cyan-400/40 focus:outline-none" /></div>
        </div>
        {feed && (
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 px-4 py-2.5" data-testid="feed-meta">
            <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${feed.is_live ? "bg-red-500/20 text-red-300" : "bg-white/10 text-white/60"}`}>{feed.is_live ? "● LIVE" : "RECORDED"}</span>
            <span className="truncate text-sm text-white/80">{feed.title}</span>
            {feed.duration ? <span className="text-[11px] text-white/40">{Math.floor(feed.duration / 60)}′{String(Math.round(feed.duration % 60)).padStart(2, "0")}</span> : null}
          </div>
        )}
        {err && !feed && <p className="mt-3 text-sm text-red-300" data-testid="video-error">{err}</p>}
      </Section>

      {feed && (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <Section id="video frame vision feed overlay" label="Vision Feed" state={analyzing ? "loading" : "ready"} testid="vision-feed-section">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] font-tac tracking-widest text-white/40"><Eye size={12} /> STEP 2 · WHAT OCTON SEES</div>
              <div className="flex items-center gap-2">
                {CADENCES.map((c) => (
                  <button key={c.v} onClick={() => setCadence(c.v)} data-testid={`cadence-${c.v}`}
                    className={`rounded-md px-2 py-1 text-[11px] ${cadence === c.v ? "bg-cyan-400/20 text-cyan-200 border border-cyan-400/40" : "text-white/45 border border-white/10 hover:text-white"}`}>{c.label}</button>
                ))}
              </div>
            </div>
            <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-white/10 bg-black">
              {result?.frame ? <img src={result.frame} alt="frame" className="h-full w-full object-contain" data-testid="vision-frame" /> :
                <div className="flex h-full items-center justify-center text-sm text-white/30">Press “Read now” or start the loop</div>}
              {result?.frame && a && a.confidence !== "low" && <Overlay ov={a.overlay} />}
              {analyzing && <div className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-black/70 px-2 py-1 text-[10px] text-cyan-200"><Loader2 size={11} className="animate-spin" /> reading…</div>}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button onClick={() => setRunning((r) => !r)} disabled={cadence === 0} data-testid="toggle-loop-btn"
                className="flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-400/15 px-4 py-2 text-sm font-semibold text-cyan-200 hover:bg-cyan-400/25 disabled:opacity-40">
                {running ? <><Pause size={15} /> Stop</> : <><Play size={15} /> Start loop</>}
              </button>
              <button onClick={analyzeOnce} disabled={analyzing} data-testid="read-now-btn"
                className="flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 hover:border-white/30 disabled:opacity-40"><Activity size={15} /> Read now</button>
              <button onClick={() => setNarrate((n) => !n)} data-testid="narrate-toggle"
                className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-[12px] ${narrate ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" : "border-white/15 text-white/50"}`}>
                {narrate ? <Volume2 size={14} /> : <VolumeX size={14} />} Narrate</button>
              {feed.is_live && (rec ? (
                <button onClick={stopRec} data-testid="stop-rec-btn"
                  className="flex items-center gap-2 rounded-xl border border-red-400/50 bg-red-500/15 px-3 py-2 text-[12px] font-semibold text-red-200 animate-pulse">
                  ● REC {recStat ? `${Math.floor(recStat.elapsed / 60)}:${String(Math.round(recStat.elapsed % 60)).padStart(2, "0")} · ${recStat.size_mb}MB` : ""} — Stop</button>
              ) : (
                <button onClick={startRec} data-testid="start-rec-btn"
                  className="flex items-center gap-2 rounded-xl border border-white/15 px-3 py-2 text-[12px] text-white/70 hover:border-red-400/40 hover:text-red-200">
                  <span className="h-2 w-2 rounded-full bg-red-400" /> Audit capture</button>
              ))}
              {!feed.is_live && <span className="text-[11px] text-white/35" data-testid="clock-readout">match clock ~{Math.floor(clockRef.current / 60)}′{String(clockRef.current % 60).padStart(2, "0")}</span>}
            </div>
          </Section>

          <Section id="tactical read advisory octon fatigue" label="OCTON Tactical Read" state={analyzing && !a ? "loading" : "ready"} testid="tactical-read-section">
            <div className="mb-3 flex items-center gap-2 text-[10px] font-tac tracking-widest text-white/40"><Zap size={12} /> STEP 3-5 · READ, FUSE & ADVISE</div>
            {!a ? <div className="flex h-40 items-center justify-center text-sm text-white/35">{analyzing ? <><Loader2 className="mr-2 animate-spin" size={16} /> OCTON is reading the match…</> : "No read yet."}</div> : (
              <div className="space-y-4" data-testid="analysis-body">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md px-2 py-0.5 text-[10px] font-bold" style={{ background: `${confColor(a.confidence)}22`, color: confColor(a.confidence) }} data-testid="confidence-badge">CONFIDENCE {(a.confidence || "?").toUpperCase()}</span>
                  {a.phase && a.phase !== "n/a" && <span className="rounded-md bg-white/8 px-2 py-0.5 text-[10px] text-white/70">{a.phase}</span>}
                  {a.in_play === false && <span className="rounded-md bg-red-500/15 px-2 py-0.5 text-[10px] text-red-300">not open play</span>}
                </div>

                {flags.length > 0 && (
                  <div className="space-y-2" data-testid="fatigue-flags">
                    {flags.map((f, i) => (
                      <div key={i} className="rounded-xl border border-red-400/30 bg-red-500/10 p-3" data-testid={`fatigue-flag-${i}`}>
                        <p className="flex items-center gap-1.5 text-sm font-bold text-red-200"><AlertTriangle size={14} /> SUBSTITUTION FLAG · {f.player}</p>
                        <p className="mt-0.5 text-[12px] text-white/70">{f.observation}</p>
                        {f.acwr_context && <p className="mt-0.5 text-[11px] text-red-200/70">OCTON load: {f.acwr_context}</p>}
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-sm text-white/85" data-testid="scene-line"><span className="text-white/40">Scene: </span>{a.scene}</p>
                {a.shapes_seen && (a.shapes_seen.team || a.shapes_seen.opponent) && <p className="text-[13px] text-white/70"><span className="text-white/40">Shapes: </span>{team}: {a.shapes_seen.team || "—"} · {opponent}: {a.shapes_seen.opponent || "—"}</p>}
                {a.ball_zone && <p className="text-[13px] text-white/70"><span className="text-white/40">Ball zone: </span>{a.ball_zone}</p>}
                {a.space_and_threat && <p className="text-[13px] text-white/70"><span className="text-white/40">Space / threat: </span>{a.space_and_threat}</p>}
                {a.tactical_read && <p className="rounded-xl border border-white/8 bg-black/30 p-3 text-sm leading-relaxed text-white/85" data-testid="tactical-read">{a.tactical_read}</p>}
                {Array.isArray(a.advisory) && a.advisory.length > 0 && (
                  <div className="space-y-2" data-testid="advisory-list">
                    <div className="flex items-center gap-2 text-[10px] font-tac tracking-widest text-cyan-300"><Zap size={12} /> LIVE ADVISORY</div>
                    {a.advisory.map((x, i) => (
                      <div key={i} className="rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3" data-testid={`advisory-${i}`}>
                        <p className="text-sm font-semibold text-white">{x.instruction}</p><p className="mt-0.5 text-[12px] text-white/55">{x.why}</p>
                      </div>
                    ))}
                  </div>
                )}
                {a.caveats && <p className="flex items-start gap-1.5 text-[11px] text-white/40" data-testid="caveats"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {a.caveats}</p>}
                {result?.octon_context && <p className="border-t border-white/8 pt-2 text-[11px] text-white/35"><span className="text-white/45">OCTON anchor data: </span>{result.octon_context.slice(0, 200)}…</p>}
              </div>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}
